import { useCallback } from "react";

import {
  useAddUserToKanbanBoardMutation,
  useRemoveUserFromKanbanBoardMutation,
  useUpdateUserKanbanBoardRoleMutation,
  UserKanbanBoardRole,
  GetKanbanBoardWithPermissionsDocument,
  GetKanbanBoardWithPermissionsQuery,
} from "@/generated/graphql";

/**
 * Interface for board sharing operations
 */
export interface UseBoardSharingResult {
  addUser: (userId: string, role: UserKanbanBoardRole) => Promise<void>;
  removeUser: (userKanbanBoardId: string) => Promise<void>;
  updateUserRole: (
    userKanbanBoardId: string,
    role: UserKanbanBoardRole
  ) => Promise<void>;
  isAddingUser: boolean;
  isRemovingUser: boolean;
  isUpdatingRole: boolean;
}

/**
 * Custom hook to manage board sharing operations (add/remove users, update roles)
 * @param boardId - The ID of the kanban board to manage sharing for
 * @returns Object with addUser, removeUser, updateUserRole functions and their loading states
 */
export const useBoardSharing = (
  boardId: string | undefined
): UseBoardSharingResult => {
  const [addUserMutation, { loading: isAddingUser }] =
    useAddUserToKanbanBoardMutation({
      // SE-3607: Refetch queries so UI updates immediately
      refetchQueries: ["ListMyKanbanBoards", "GetKanbanBoardWithPermissions"],
      // Use cache update instead of refetch to work around backend bug (SE-3569)
      // where getKanbanBoard.users doesn't return users for ROSTER boards
      update(cache, { data }) {
        if (!data?.addUserToKanbanBoard || !boardId) return;

        const newUserKanbanBoard = data.addUserToKanbanBoard;

        // Read existing query from cache
        const existingData =
          cache.readQuery<GetKanbanBoardWithPermissionsQuery>({
            query: GetKanbanBoardWithPermissionsDocument,
            variables: { id: boardId, first: 100 },
          });

        if (!existingData?.getKanbanBoard) return;

        // Add the new user to the users connection
        const existingEdges = existingData.getKanbanBoard.users?.edges ?? [];

        // Check if user already exists to avoid duplicates
        const userExists = existingEdges.some(
          edge => edge.node.userId === newUserKanbanBoard.userId
        );

        if (userExists) return;

        // Write updated data back to cache
        cache.writeQuery<GetKanbanBoardWithPermissionsQuery>({
          query: GetKanbanBoardWithPermissionsDocument,
          variables: { id: boardId, first: 100 },
          data: {
            ...existingData,
            getKanbanBoard: {
              ...existingData.getKanbanBoard,
              users: {
                ...existingData.getKanbanBoard.users,
                edges: [
                  ...existingEdges,
                  {
                    __typename: "UserKanbanBoardEdge" as const,
                    cursor: newUserKanbanBoard.id,
                    node: newUserKanbanBoard,
                  },
                ],
              },
            },
          },
        });
      },
    });

  const [removeUserMutation, { loading: isRemovingUser }] =
    useRemoveUserFromKanbanBoardMutation({
      // SE-3607: Refetch queries so UI updates immediately
      refetchQueries: ["ListMyKanbanBoards", "GetKanbanBoardWithPermissions"],
      // Use cache update for remove as well
      update(cache, { data }) {
        if (!data?.removeUserFromKanbanBoard || !boardId) return;

        const removedUser = data.removeUserFromKanbanBoard;

        // Read existing query from cache
        const existingData =
          cache.readQuery<GetKanbanBoardWithPermissionsQuery>({
            query: GetKanbanBoardWithPermissionsDocument,
            variables: { id: boardId, first: 100 },
          });

        if (!existingData?.getKanbanBoard) return;

        // Filter out the removed user
        const existingEdges = existingData.getKanbanBoard.users?.edges ?? [];
        const updatedEdges = existingEdges.filter(
          edge => edge.node.id !== removedUser.id
        );

        // Write updated data back to cache
        cache.writeQuery<GetKanbanBoardWithPermissionsQuery>({
          query: GetKanbanBoardWithPermissionsDocument,
          variables: { id: boardId, first: 100 },
          data: {
            ...existingData,
            getKanbanBoard: {
              ...existingData.getKanbanBoard,
              users: {
                ...existingData.getKanbanBoard.users,
                edges: updatedEdges,
              },
            },
          },
        });
      },
    });

  const [updateRoleMutation, { loading: isUpdatingRole }] =
    useUpdateUserKanbanBoardRoleMutation({
      // SE-3607: Refetch queries so UI updates immediately
      refetchQueries: ["ListMyKanbanBoards", "GetKanbanBoardWithPermissions"],
      // Role updates are handled automatically by Apollo cache normalization
      // since the mutation returns the updated UserKanbanBoard
    });

  const addUser = useCallback(
    async (userId: string, role: UserKanbanBoardRole) => {
      if (!boardId) {
        throw new Error("Board ID is required");
      }

      await addUserMutation({
        variables: {
          input: {
            kanbanBoardId: boardId,
            userId,
            role,
          },
        },
      });
    },
    [boardId, addUserMutation]
  );

  const removeUser = useCallback(
    async (userKanbanBoardId: string) => {
      if (!boardId) {
        throw new Error("Board ID is required");
      }

      await removeUserMutation({
        variables: {
          input: {
            id: userKanbanBoardId,
          },
        },
      });
    },
    [boardId, removeUserMutation]
  );

  const updateUserRole = useCallback(
    async (userKanbanBoardId: string, role: UserKanbanBoardRole) => {
      if (!boardId) {
        throw new Error("Board ID is required");
      }

      await updateRoleMutation({
        variables: {
          input: {
            id: userKanbanBoardId,
            role,
          },
        },
      });
    },
    [boardId, updateRoleMutation]
  );

  return {
    addUser,
    removeUser,
    updateUserRole,
    isAddingUser,
    isRemovingUser,
    isUpdatingRole,
  };
};
